import React, { useState, useEffect } from 'react';
import { 
  Pill, Clock, CheckCircle, X, Calendar, ArrowLeft, 
  AlertCircle, Plus, ChevronDown, ChevronUp, Edit2, Trash2 
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { PageHeader } from '../common/PageHeader';
import { LoadingState } from '../common/LoadingState';
import { AlertCard } from '../common/AlertCard';
import { EmptyState } from '../common/EmptyState';
import { format, isAfter, subDays } from 'date-fns';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { PatientLayout } from './PatientLayout';
import { medicationsApi } from '../../lib/api/medications';
import { useAuth } from '../../lib/hooks/useAuth';
import { Alert, AlertDescription } from '../ui/alert';

interface MedicationLogProps {
  onBack?: () => void;
  onNavigate?: (view: string) => void;
  onLogout?: () => void;
}

const MedicationLogComponent: React.FC<MedicationLogProps> = ({ onBack, onNavigate, onLogout }) => {
  // ✅ PERMANENT FIX: No auth blocking - let API handle authentication
  const { user, isLoading: authLoading } = useAuth();
  
  // Local state for medications data
  const [medicationsData, setMedicationsData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [patientId, setPatientId] = useState<number | null>(null);

  // Local state
  const [showLogModal, setShowLogModal] = useState(false);
  const [selectedMed, setSelectedMed] = useState<any>(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [logNotes, setLogNotes] = useState('');
  const [actualTime, setActualTime] = useState(format(new Date(), 'HH:mm'));
  const [expandedMed, setExpandedMed] = useState<number | null>(null);

  // Edit/Delete state
  const [medicationLogs, setMedicationLogs] = useState<any[]>([]);
  const [editingLog, setEditingLog] = useState<any | null>(null);
  const [deletingLog, setDeletingLog] = useState<any | null>(null);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);

  // Form state for editing
  const [editTakenTime, setEditTakenTime] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editStatus, setEditStatus] = useState<'taken' | 'missed' | 'skipped'>('taken');
  
  // ✅ FIX: Add loading state for logging medication
  const [isLoggingMedication, setIsLoggingMedication] = useState(false);

  // Fetch medications (using /me endpoint - backend handles auth)
  const fetchMedications = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // ✅ FIXED: Use /me endpoint that doesn't need patient_id
      const response = await medicationsApi.getMyMedications();
      console.log('💊 MedicationLog - API Response:', response);
      setMedicationsData(response);
    } catch (err: any) {
      console.error('❌ MedicationLog - Failed to fetch medications:', err);
      setError(err);
      setMedicationsData(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch medication logs (last 7 days)
  const fetchMedicationLogs = async () => {
    try {
      setIsLoadingLogs(true);
      const response = await medicationsApi.getMyMedicationLogs(7);
      setMedicationLogs(response.logs || []);
      // ✅ Extract patient_id from response for logging medication
      if (response.patient_id) {
        setPatientId(response.patient_id);
      }
    } catch (err) {
      console.error('Failed to fetch medication logs:', err);
      setMedicationLogs([]);
    } finally {
      setIsLoadingLogs(false);
    }
  };

  // Fetch on mount - no dependencies needed, /me endpoint handles auth
  useEffect(() => {
    fetchMedications();
    fetchMedicationLogs();
  }, []);

  const refetch = () => {
    fetchMedications();
    fetchMedicationLogs();
  };

  // ✅ FIX: Only show loading during initial auth check
  if (authLoading) {
    return (
      <PatientLayout currentView="medication-log" onNavigate={onNavigate} onLogout={onLogout}>
        <LoadingState message="Loading..." />
      </PatientLayout>
    );
  }

  // ✅ FIX: Don't block on missing user/patient_id - let the API handle authentication
  // The backend will return 401 if not authenticated, which we handle in error state below
  // This prevents false "Not Authenticated" messages when user object is still loading

  // ✅ FIX: Data loading state (after auth checks)
  if (isLoading) {
    return (
      <PatientLayout currentView="medication-log" onNavigate={onNavigate} onLogout={onLogout}>
        <LoadingState message="Loading your medications..." />
      </PatientLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <PatientLayout currentView="medication-log" onNavigate={onNavigate} onLogout={onLogout}>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <AlertCard
            type="error"
            title="Failed to load medications"
            description={error.message || 'There was an error loading your medications. Please try again.'}
            action={
              <Button onClick={() => refetch()} variant="outline">
                Try Again
              </Button>
            }
          />
        </div>
      </PatientLayout>
    );
  }

  // Empty state
  if (!medicationsData?.medications || medicationsData.medications.length === 0) {
    return (
      <PatientLayout currentView="medication-log" onNavigate={onNavigate} onLogout={onLogout}>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <EmptyState
            icon={Pill}
            title="No medications scheduled"
            description="You don't have any medications scheduled yet. Your doctor will prescribe medications if needed."
          />
        </div>
      </PatientLayout>
    );
  }

  const medications = medicationsData.medications;
  const todayLogs = medicationsData.today_logs;
  const adherenceData = medicationsData.adherence_summary;

  const getAdherenceColor = (adherence: number) => {
    if (adherence >= 90) return '#14CAF4';
    if (adherence >= 75) return '#F07D13';
    return '#EF4444';
  };

  const getAdherenceStatus = (adherence: number) => {
    if (adherence >= 90) return { label: 'Excellent', risk: 'Low Risk' };
    if (adherence >= 75) return { label: 'Good', risk: 'Moderate Risk' };
    return { label: 'Poor', risk: 'High Risk' };
  };

  const handleLogMedication = (med: any, scheduledTime: string) => {
    setSelectedMed(med);
    setSelectedTime(scheduledTime);
    setActualTime(format(new Date(), 'HH:mm'));
    setLogNotes('');
    setShowLogModal(true);
  };

  const confirmLogMedication = async () => {
    if (!selectedMed) {
      toast.error('No medication selected');
      return;
    }

    try {
      setIsLoggingMedication(true);
      await medicationsApi.logMyMedication({
        medication_schedule_id: selectedMed.id,
        scheduled_time: selectedTime,
        taken_time: actualTime,
        status: 'TAKEN',  // ✅ FIX: Backend expects uppercase TAKEN, not lowercase taken
        notes: logNotes || undefined,
      });

      toast.success('Medication logged successfully');
      setShowLogModal(false);
      setSelectedMed(null);
      refetch(); // Refresh all data
    } catch (err: any) {
      toast.error(err?.message || 'Failed to log medication');
    } finally {
      setIsLoggingMedication(false);
    }
  };

  const toggleMedicationDetails = (medId: number) => {
    setExpandedMed(prev => prev === medId ? null : medId);
  };

  const getStatusBadge = (status: string) => {
    // ✅ FIX: Backend returns uppercase, handle both cases
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case 'taken':
        return <Badge className="bg-[#14CAF4] text-white">Taken</Badge>;
      case 'missed':
        return <Badge className="bg-destructive text-white">Missed</Badge>;
      case 'skipped':
        return <Badge className="bg-muted text-muted-foreground">Skipped</Badge>;
      default:
        return <Badge className="bg-[#F07D13] text-white">Pending</Badge>;
    }
  };

  // Edit functions
  const openEditDialog = (log: any) => {
    setEditingLog(log);
    setEditTakenTime(log.taken_time || '');
    setEditNotes(log.notes || '');
    // ✅ FIX: Backend returns uppercase, convert to lowercase for UI state
    setEditStatus((log.status?.toLowerCase() || 'taken') as 'taken' | 'missed' | 'skipped');
  };

  const handleEditLog = async () => {
    if (!editingLog) return;

    try {
      await medicationsApi.updateMyMedicationLog(editingLog.id, {
        taken_time: editTakenTime,
        status: editStatus.toUpperCase() as 'TAKEN' | 'MISSED' | 'SKIPPED',  // ✅ FIX: Convert to uppercase for backend
        notes: editNotes || undefined,
      });

      toast.success('Medication log updated successfully!');
      setEditingLog(null);
      fetchMedicationLogs();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update medication log');
    }
  };

  const handleDeleteLog = async () => {
    if (!deletingLog) return;

    try {
      await medicationsApi.deleteMyMedicationLog(deletingLog.id);
      toast.success('Medication log deleted successfully!');
      setDeletingLog(null);
      fetchMedicationLogs();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete medication log');
    }
  };

  // Check if log is within last 7 days
  const isEditable = (logDate: string) => {
    const sevenDaysAgo = subDays(new Date(), 7);
    const recordDate = new Date(logDate);
    return isAfter(recordDate, sevenDaysAgo);
  };

  const adherence = adherenceData.week_adherence;
  const status = getAdherenceStatus(adherence);

  const breadcrumbs = [
    { label: 'Home', href: '#' },
    { label: 'Medication Tracker', href: '#' },
  ];

  // Generate today's schedule combining medications and logs
  const generateTodaySchedule = () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    const schedule: any[] = [];

    medications.forEach((med: any) => {
      med.scheduled_times.split(",").forEach((time: string) => {
        const existingLog = todayLogs.find(
          (log: any) => log.medication_id === med.id && log.scheduled_time === time
        );

        schedule.push({
          id: `${med.id}-${time}`,
          medicationId: med.id,
          medicationName: med.medication_name,
          dosage: med.dosage,
          scheduledTime: time,
          status: existingLog?.status || 'pending',
          takenTime: existingLog?.taken_time,
          notes: existingLog?.notes,
          date: today,
        });
      });
    });

    return schedule.sort((a, b) => a.scheduledTime.localeCompare(b.scheduledTime));
  };

  const todaySchedule = generateTodaySchedule();

  return (
    <PatientLayout 
      currentView="medication-log"
      onNavigate={onNavigate}
      hideTopNav = {user.role==="NURSE"}
      onLogout={onLogout}
    >
      {user.role==="PATIENT" && (<PageHeader 
        title="Medication Tracker" 
        subtitle="Log and track your daily medications"
        breadcrumbs={breadcrumbs}
      />)}

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Back Button */}
        {onBack && (
          <Button variant="outline" onClick={onBack} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back to Dashboard</span>
            <span className="sm:hidden">Back</span>
          </Button>
        )}

        {/* Adherence Score Card */}
        <Card 
          className="border-l-4 shadow-lg" 
          style={{ borderLeftColor: getAdherenceColor(adherence) }}
        >
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Pill className="w-5 h-5" style={{ color: getAdherenceColor(adherence) }} />
                7-Day Medication Adherence
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Adherence Score */}
              <div className="text-center">
                <div 
                  className="text-5xl mb-2"
                  style={{ color: getAdherenceColor(adherence) }}
                >
                  {Math.round(adherence)}%
                </div>
                <p className="text-muted-foreground">Adherence Rate</p>
                <Badge 
                  className="mt-2"
                  style={{ 
                    backgroundColor: getAdherenceColor(adherence),
                    color: '#fff'
                  }}
                >
                  {status.label}
                </Badge>
              </div>

              {/* Risk Indicator */}
              <div className="text-center border-l border-r px-4">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <AlertCircle 
                    className="w-8 h-8" 
                    style={{ color: getAdherenceColor(adherence) }}
                  />
                </div>
                <p className="text-muted-foreground mb-2">Risk Level</p>
                <p 
                  className="text-lg"
                  style={{ color: getAdherenceColor(adherence) }}
                >
                  {status.risk}
                </p>
                {adherence < 90 && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {adherence < 75 
                      ? 'Please maintain medication schedule' 
                      : 'Aim for 90%+ adherence'}
                  </p>
                )}
              </div>

              {/* Quick Stats */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Taken (7 days):</span>
                  <span className="text-[#14CAF4]">{adherenceData.total_taken}/{adherenceData.total_scheduled}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Missed:</span>
                  <span className="text-destructive">{adherenceData.total_missed}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Skipped:</span>
                  <span className="text-muted-foreground">{adherenceData.total_skipped}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                  <div 
                    className="h-2 rounded-full transition-all"
                    style={{ 
                      width: `${adherence}%`,
                      backgroundColor: getAdherenceColor(adherence)
                    }}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Today's Medication Schedule */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ color: '#2762AC' }}>
              <Calendar className="w-5 h-5" />
              Today's Schedule
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {format(new Date(), 'EEEE, MMMM dd, yyyy')}
            </p>
          </CardHeader>
          <CardContent>
            {todaySchedule.length === 0 ? (
              <EmptyState
                icon={Calendar}
                title="No medications scheduled for today"
                description="You don't have any medications scheduled for today."
              />
            ) : (
              <div className="space-y-3">
                {todaySchedule.map((log: any) => {
                  const medication = medications.find((m: any) => m.id === log.medicationId);
                  if (!medication) return null;

                  return (
                    <div 
                      key={log.id}
                      className="p-4 border rounded-lg hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1">
                          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Pill className="w-6 h-6 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-medium">{log.medicationName}</h4>
                              {getStatusBadge(log.status)}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {log.dosage} • Scheduled: {log.scheduledTime}
                            </p>
                            {log.takenTime && (
                              <p className="text-sm text-[#14CAF4] flex items-center gap-1 mt-1">
                                <CheckCircle className="w-3 h-3" />
                                Taken at {log.takenTime}
                              </p>
                            )}
                            {log.notes && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Note: {log.notes}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          {log.status === 'pending' && (
                            <Button
                              size="sm"
                              style={{ backgroundColor: '#14CAF4' }}
                              className="text-white hover:opacity-90"
                              onClick={() => handleLogMedication(medication, log.scheduledTime)}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Log Now
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Medication History (Last 7 Days) */}
        <Card className="shadow-lg">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2" style={{ color: '#2762AC' }}>
                <Clock className="w-5 h-5" />
                Medication History (Last 7 Days)
              </CardTitle>
            </div>
            <Alert className="mt-3">
              <AlertCircle className="w-4 h-4" />
              <AlertDescription>
                You can edit or delete medication logs from the last 7 days only.
              </AlertDescription>
            </Alert>
          </CardHeader>
          <CardContent>
            {isLoadingLogs ? (
              <LoadingState message="Loading medication history..." />
            ) : medicationLogs.length === 0 ? (
              <EmptyState
                icon={Pill}
                title="No medication logs yet"
                description="Start logging your medications to see your history here."
              />
            ) : (
              <div className="space-y-3">
                {medicationLogs.map((log: any) => {
                  const editable = isEditable(log.date);
                  
                  return (
                    <div 
                      key={log.id}
                      className="p-4 border rounded-lg hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1">
                          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Pill className="w-6 h-6 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <h4 className="font-medium">{log.medication_name}</h4>
                              {getStatusBadge(log.status)}
                              {!editable && <Badge variant="outline">Read-only</Badge>}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {format(new Date(log.date), 'MMM dd, yyyy')} • Scheduled: {log.scheduled_time}
                            </p>
                            {log.taken_time && (
                              <p className="text-sm text-[#14CAF4] flex items-center gap-1 mt-1">
                                <CheckCircle className="w-3 h-3" />
                                Taken at {log.taken_time}
                              </p>
                            )}
                            {log.notes && (
                              <p className="text-xs text-muted-foreground mt-1 italic">
                                Note: {log.notes}
                              </p>
                            )}
                          </div>
                        </div>
                        {editable && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditDialog(log)}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-destructive hover:text-destructive"
                              onClick={() => setDeletingLog(log)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Prescribed Medications List */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ color: '#2762AC' }}>
              <Pill className="w-5 h-5" />
              Prescribed Medications
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {medications.map((med: any) => (
                <div 
                  key={med.id}
                  className="border rounded-lg overflow-hidden"
                >
                  <div 
                    className="p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => toggleMedicationDetails(med.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Pill className="w-5 h-5 text-primary" />
                        <div>
                          <h4 className="font-medium">{med.medication_name}</h4>
                          <p className="text-sm text-muted-foreground">
                            {med.dosage} • {med.frequency_display}
                          </p>
                        </div>
                      </div>
                      {expandedMed === med.id ? (
                        <ChevronUp className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      )}
                    </div>
                  </div>
                  {expandedMed === med.id && (
                    <div className="p-4 bg-white space-y-3">
                      <div>
                        <p className="text-sm text-muted-foreground">Category:</p>
                        <Badge variant="outline" className="mt-1">{med.category}</Badge>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Schedule Times:</p>
                        <div className="flex gap-2 mt-1 flex-wrap">
                          {med.scheduled_times.map((time: string, idx: number) => (
                            <Badge key={idx} variant="outline" className="gap-1">
                              <Clock className="w-3 h-3" />
                              {time}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      {med.instructions && (
                        <div>
                          <p className="text-sm text-muted-foreground">Instructions:</p>
                          <p className="text-sm mt-1">{med.instructions}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-sm text-muted-foreground">Prescribed By:</p>
                        <p className="text-sm mt-1">{med.prescribed_by}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Start Date:</p>
                        <p className="text-sm mt-1">{format(new Date(med.start_date), 'PPP')}</p>
                      </div>
                      {med.end_date && (
                        <div>
                          <p className="text-sm text-muted-foreground">End Date:</p>
                          <p className="text-sm mt-1">{format(new Date(med.end_date), 'PPP')}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Log Medication Modal */}
      <Dialog open={showLogModal} onOpenChange={setShowLogModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: '#2762AC' }}>
              <Pill className="w-5 h-5" />
              Log Medication
            </DialogTitle>
            <DialogDescription>
              Record when you took your medication
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Medication</Label>
              <p className="text-sm font-medium">{selectedMed?.medication_name}</p>
              <p className="text-xs text-muted-foreground">{selectedMed?.dosage}</p>
            </div>

            <div className="space-y-2">
              <Label>Scheduled Time</Label>
              <p className="text-sm">{selectedTime}</p>
            </div>

            <div className="space-y-2">
              <Label>Time Taken</Label>
              <Input 
                type="time"
                value={actualTime}
                onChange={(e) => setActualTime(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea 
                placeholder="Any side effects or observations..."
                value={logNotes}
                onChange={(e) => setLogNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowLogModal(false)}
              disabled={isLoggingMedication}
            >
              Cancel
            </Button>
            <Button
              style={{ backgroundColor: '#14CAF4' }}
              className="text-white hover:opacity-90"
              onClick={confirmLogMedication}
              disabled={isLoggingMedication}
            >
              {isLoggingMedication ? 'Logging...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Medication Log Modal */}
      <Dialog open={!!editingLog} onOpenChange={(open) => !open && setEditingLog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: '#2762AC' }}>
              <Edit2 className="w-5 h-5" />
              Edit Medication Log
            </DialogTitle>
            <DialogDescription>
              Update your medication intake record
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Medication</Label>
              <p className="text-sm font-medium">{editingLog?.medication_name}</p>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={editStatus === 'taken' ? 'default' : 'outline'}
                  onClick={() => setEditStatus('taken')}
                >
                  Taken
                </Button>
                <Button
                  size="sm"
                  variant={editStatus === 'missed' ? 'default' : 'outline'}
                  onClick={() => setEditStatus('missed')}
                >
                  Missed
                </Button>
                <Button
                  size="sm"
                  variant={editStatus === 'skipped' ? 'default' : 'outline'}
                  onClick={() => setEditStatus('skipped')}
                >
                  Skipped
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Time Taken</Label>
              <Input 
                type="time"
                value={editTakenTime}
                onChange={(e) => setEditTakenTime(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea 
                placeholder="Any side effects or observations..."
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setEditingLog(null)}
            >
              Cancel
            </Button>
            <Button
              style={{ backgroundColor: '#14CAF4' }}
              className="text-white hover:opacity-90"
              onClick={handleEditLog}
            >
              Update Log
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingLog} onOpenChange={(open) => !open && setDeletingLog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Medication Log?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this medication log record. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteLog}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PatientLayout>
  );
};

export { MedicationLogComponent as MedicationLog };
export default MedicationLogComponent;